import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '@/components/pos/intl-locale';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import POSLayout from '@/components/layout/POSLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatUZS } from '@/lib/format';
import { useMerchantId } from '@/hooks/useMerchantId';
import { useAuth } from '@/context/AuthContext';
import { useCashierBranchId } from '@/hooks/api/branches';
import { toast } from 'sonner';
import { Play, Square, Printer, RefreshCw } from 'lucide-react';
import { apiErrorMessage } from '@/lib/api-error';

type Shift = {
  id: number;
  opened_at: string;
  closed_at: string | null;
  status: string;
  opening_cash: string;
};

type ShiftReport = {
  salesCount: number;
  total: number;
  byMethod: Record<string, number>;
  refunds: number;
  refundsCount: number;
  expectedCash: number;
};

export default function Shift() {
  const { t } = useTranslation();
  const { merchantId } = useMerchantId();
  const { user } = useAuth();
  const cashierBranchId = useCashierBranchId();
  const queryClient = useQueryClient();
  const [openingCash, setOpeningCash] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: shift, isLoading } = useQuery({
    queryKey: ['pos-shift-open', merchantId, user?.id],
    queryFn: async (): Promise<Shift | null> => {
      const { data, error } = await apiClient.GET('/pos-shifts');
      if (error) throw error;
      const open = (data?.data ?? []).find(s => s.status === 'open');
      return (open as unknown as Shift) ?? null;
    },
    enabled: !!merchantId,
  });

  const { data: report, refetch: refetchReport } = useQuery({
    queryKey: ['pos-shift-report', shift?.id],
    queryFn: async (): Promise<ShiftReport> => {
      const { data, error } = await apiClient.GET('/pos-shifts/{posShift}/report', {
        params: { path: { posShift: shift!.id } },
      });
      if (error) throw error;
      const r = data!.data;

      return {
        salesCount: r.sales_count,
        total: r.sales_total,
        byMethod: r.by_method as Record<string, number>,
        refunds: r.refunds_total,
        refundsCount: r.refunds_count,
        expectedCash: r.expected_cash,
      };
    },
    enabled: !!shift?.id && !!merchantId,
  });

  const methodLabel = (m: string) =>
    m === 'cash' ? t('pos_pay.cash') : m === 'card' ? t('pos_pay.card') : m === 'hisob_wallet' ? t('pos_pay.wallet') : m === 'murabaha' ? t('pos_pay.nasiya') : m;

  const handleOpen = async () => {
    if (!merchantId) return;
    setBusy(true);
    const branchId = cashierBranchId ?? null;
    const { error } = await apiClient.POST('/pos-shifts', {
      body: { branch_id: branchId, opening_cash: Number(openingCash || 0) },
    });
    setBusy(false);
    if (error) {
      toast.error(apiErrorMessage(error, t('pos_shift.open_error')));
      return;
    }
    setOpeningCash('');
    toast.success(t('pos_shift.opened'));
    queryClient.invalidateQueries({ queryKey: ['pos-shift-open'] });
  };

  const handleClose = async () => {
    if (!shift || !report) return;
    if (closingCash === '') {
      toast.error(t('pos_shift.enter_cash'));
      return;
    }
    setBusy(true);
    const closing = Number(closingCash);
    const { error } = await apiClient.POST('/pos-shifts/{posShift}/close', {
      params: { path: { posShift: shift.id } },
      body: { closing_cash: closing, note },
    });
    setBusy(false);
    if (error) {
      toast.error(apiErrorMessage(error, t('pos_shift.close_error')));
      return;
    }
    setClosingCash('');
    setNote('');
    toast.success(t('pos_shift.closed'));
    queryClient.invalidateQueries({ queryKey: ['pos-shift-open'] });
  };

  const printReport = (type: 'X' | 'Z') => {
    if (!shift || !report) return;
    const w = window.open('', '_blank', 'width=320,height=640');
    if (!w) return;
    const num = (v: number) => new Intl.NumberFormat(intlLocale()).format(Math.round(v || 0));
    const rows = Object.entries(report.byMethod)
      .map(([m, v]) => `<div class="row"><span>${methodLabel(m)}</span><span>${num(v)}</span></div>`)
      .join('');
    w.document.write(`<!DOCTYPE html><html><head><title>${t('pos_shift.report_title', { type })}</title><style>
      @page{margin:0;size:80mm auto}*{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Courier New',monospace;width:80mm;padding:4mm;font-size:12px}
      .center{text-align:center}.bold{font-weight:bold}.divider{border-top:1px dashed #000;margin:6px 0}
      .row{display:flex;justify-content:space-between;padding:2px 0}
    </style></head><body>
      <div class="center bold">${t('pos_shift.report_title_caps', { type })}</div>
      <div class="center">${t('pos_shift.shift_n', { id: shift.id })}</div>
      <div class="center">${t('pos_shift.opened_at', { date: new Date(shift.opened_at).toLocaleString(intlLocale()) })}</div>
      <div class="divider"></div>
      <div class="row"><span>${t('pos_shift.sales')}</span><span>${t('pos_common.count_pcs', { n: report.salesCount })}</span></div>
      <div class="row"><span>${t('pos_shift.total_revenue')}</span><span>${num(report.total)}</span></div>
      <div class="divider"></div>${rows}<div class="divider"></div>
      <div class="row"><span>${t('pos_shift.refunds')}</span><span>${num(report.refunds)}</span></div>
      <div class="row"><span>${t('pos_shift.opening_cash')}</span><span>${num(Number(shift.opening_cash))}</span></div>
      <div class="row bold"><span>${t('pos_shift.expected_cash')}</span><span>${num(report.expectedCash)}</span></div>
      <div class="divider"></div>
      <div class="center">${new Date().toLocaleString(intlLocale())}</div>
      <script>window.onload=function(){window.print();window.close();}</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <POSLayout>
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-foreground">{t('pos_shift.title')}</h1>
          {shift && <Badge variant="secondary">{t('pos_shift.open_badge')}</Badge>}
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('pos_shift.loading')}</p>
        ) : !shift ? (
          <Card className="p-5 space-y-4">
            <p className="text-sm text-muted-foreground">{t('pos_shift.none_open')}</p>
            <div className="space-y-2">
              <Label htmlFor="opening">{t('pos_shift.opening_cash_label')}</Label>
              <Input id="opening" inputMode="numeric" value={openingCash} onChange={(e) => setOpeningCash(e.target.value.replace(/\D/g, ''))} placeholder="0" />
            </div>
            {/* Filial yuklanmaguncha tugma o'chiq — aks holda branch_id bo'sh ketib, smena ochilmay qoladi */}
            <Button className="w-full" onClick={handleOpen} disabled={busy || !merchantId || cashierBranchId == null}>
              <Play size={16} className="mr-2" /> {t('pos_shift.open_btn')}
            </Button>
          </Card>
        ) : (
          <>
            <Card className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{t('pos_shift.shift_n', { id: shift.id })}</p>
                  <p className="text-xs text-muted-foreground">{t('pos_shift.opened_at', { date: new Date(shift.opened_at).toLocaleString(intlLocale()) })}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => refetchReport()}>
                  <RefreshCw size={14} className="mr-1" /> {t('pos_shift.refresh')}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground">{t('pos_shift.sales')}</p>
                  <p className="text-lg font-semibold text-foreground">{t('pos_common.count_pcs', { n: report?.salesCount ?? 0 })}</p>
                </div>
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground">{t('pos_shift.total_revenue')}</p>
                  <p className="text-lg font-semibold text-foreground">{formatUZS(report?.total ?? 0)}</p>
                </div>
              </div>
              <div className="space-y-1">
                {Object.entries(report?.byMethod ?? {}).map(([m, v]) => (
                  <div key={m} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{methodLabel(m)}</span>
                    <span className="text-foreground">{formatUZS(v)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('pos_shift.refunds_n', { n: report?.refundsCount ?? 0 })}</span>
                  <span className="text-foreground">-{formatUZS(report?.refunds ?? 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('pos_shift.opening_cash')}</span>
                  <span className="text-foreground">{formatUZS(Number(shift.opening_cash))}</span>
                </div>
                <div className="flex justify-between text-sm font-medium border-t border-border pt-2 mt-2">
                  <span className="text-foreground">{t('pos_shift.expected_cash')}</span>
                  <span className="text-foreground">{formatUZS(report?.expectedCash ?? 0)}</span>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => printReport('X')}>
                <Printer size={16} className="mr-2" /> {t('pos_shift.x_report')}
              </Button>
            </Card>

            <Card className="p-5 space-y-4">
              <p className="text-sm font-medium text-foreground">{t('pos_shift.close_title')}</p>
              <div className="space-y-2">
                <Label htmlFor="closing">{t('pos_shift.closing_cash_label')}</Label>
                <Input id="closing" inputMode="numeric" value={closingCash} onChange={(e) => setClosingCash(e.target.value.replace(/\D/g, ''))} placeholder="0" />
                {closingCash !== '' && report && (
                  <p className={`text-xs ${Number(closingCash) - report.expectedCash === 0 ? 'text-muted-foreground' : 'text-destructive'}`}>
                    {t('pos_shift.difference', { amount: formatUZS(Number(closingCash) - report.expectedCash) })}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="note">{t('pos_shift.note')}</Label>
                <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('pos_shift.optional')} rows={2} />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => printReport('Z')}>
                  <Printer size={16} className="mr-2" /> {t('pos_shift.print_z')}
                </Button>
                <Button variant="destructive" className="flex-1" onClick={handleClose} disabled={busy}>
                  <Square size={16} className="mr-2" /> {t('pos_shift.close_btn')}
                </Button>
              </div>
            </Card>
          </>
        )}
      </div>
    </POSLayout>
  );
}
